import '../../styles/_circle.scss';
import './customFileUploader.scss';

import React, { useEffect, useState, useCallback, forwardRef } from 'react';
import classNames from 'classnames';
import { useDropzone } from 'react-dropzone';
import cryptoJS from 'crypto-js';
import { formatText } from 'core/i18n';
import { DOCUMENTS_TO_BE_CAPTURED } from 'constants/documents';
import pdfImage from 'assets/icons/icon-pdf.svg';
import { doTagOnClickUploadBox, doTagOnRemoveFile } from './tagging';
import { CreateUploadBoxContent } from './CreateUploadBoxContent/CreateUploadBoxContent';

const INITIAL_STATE = {
    isFileLoading: false,
    isFileLoaded: false,
    filePath: null,
    isFileError: false,
    percentage: 0,
    message: '',
    fileName: '',
    filePreview: '',
    fileType: '',
    currentDisabled: false,
    fileDropped: false,
};

const MAX_FILE_SIZE_ALLOWED = 10485760;

export const CustomFileUploaderComponent = forwardRef(
    (
        {
            disabled,
            docId,
            documentationActions,
            fileType,
            formActions,
            handleClicToShowModal,
            handleFileReceived,
            handleRemovedFile,
            handleValidation,
            id,
            profileModel,
            title,
            userProfile,
        },
        dropzoneRef
    ) => {
        const [customFileUploaderState, setCustomFileUploaderState] = useState(INITIAL_STATE);
        const { docNumber, accessToken } = userProfile;
        const {
            fileName,
            percentage,
            isFileError,
            isFileLoaded,
            isFileLoading,
            currentDisabled,
            fileDropped,
            filePreview,
        } = customFileUploaderState;
        const shouldBeCaptureType = DOCUMENTS_TO_BE_CAPTURED.includes(docId);

        useEffect(() => {
            return () => {
                URL.revokeObjectURL(filePreview);
            };
        }, [filePreview]);

        const handleOnChangeStatus = useCallback(
            fileName => {
                if (profileModel !== null && formActions !== null) {
                    const modelProp = profileModel.split('.')[1];
                    formActions.change(`userProfile.${modelProp}`, fileName);
                    formActions.setValidity(`userProfile.${modelProp}`, { required: true });
                }
            },
            [formActions, profileModel]
        );

        const successUpload = useCallback(
            file => {
                setCustomFileUploaderState(prevState => ({
                    ...prevState,
                    isFileLoading: false,
                    isFileLoaded: true,
                    percentage: 0,
                    isFileError: false,
                    fileName: file.name,
                    fileType: file.type,
                    filePreview: '',
                }));

                handleOnChangeStatus(file.name);
                handleValidation({ errorType: '', id });
            },
            [handleOnChangeStatus, handleValidation, id]
        );

        const unauthorizedUpload = useCallback(() => {
            handleValidation({ errorType: '6002', id });
        }, [handleValidation, id]);

        const loadingFile = percent => {
            setCustomFileUploaderState(prevState => ({
                ...prevState,
                fileDropped: false,
                isFileLoading: true,
                percentage: percent === 100 ? percent - 1 : percent,
            }));
        };

        const loadedFile = (path, name) => {
            setCustomFileUploaderState(prevState => ({
                ...prevState,
                filePath: path,
                message: name,
            }));
        };

        const errorOnUpload = useCallback(() => {
            setCustomFileUploaderState(prevState => ({
                ...prevState,
                isFileError: true,
                message: formatText('uploads-wrongLoad'),
                isFileLoading: false,
                isFileLoaded: false,
                filePath: null,
                percentage: 0,
                fileName: '',
                filePreview: '',
            }));

            handleValidation({ errorType: 'other', id, docTitle: title });
        }, [handleValidation, id, title]);

        const createPreview = file => {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = () => {
                setCustomFileUploaderState(prevState => ({
                    ...prevState,
                    fileName: file.name,
                    filePreview: URL.createObjectURL(file),
                    fileType: file.type,
                }));
            };
        };

        const uploadFile = useCallback(
            file => {
                createPreview(file);
                handleFileReceived(file, docId, {
                    successUpload,
                    unauthorizedUpload,
                    errorOnUpload,
                    loadingFile,
                    loadedFile,
                });
                handleValidation({ errorType: '', id, docTitle: title });
            },
            [docId, handleFileReceived, handleValidation, errorOnUpload, unauthorizedUpload, successUpload, title]
        );

        const handleRemoveFile = event => {
            event.stopPropagation();
            const docNumberEncrypted = docNumber ? cryptoJS.SHA256(docNumber).toString() : '';
            setCustomFileUploaderState(prevState => ({
                ...prevState,
                isFileLoaded: false,
                fileDropped: false,
                filePath: null,
                message: '',
                fileName: '',
                currentDisabled: false,
                filePreview: '',
            }));
            handleOnChangeStatus('');
            documentationActions.deleteFile({ fileName, fileType: docId, accessToken });

            doTagOnRemoveFile({ userProfile, docNumberEncrypted, title });
            handleRemovedFile({ fileName, docId });
        };

        const handleCancelUploadFile = e => {
            e.preventDefault();
            setCustomFileUploaderState(prevState => ({
                ...prevState,
                isFileLoading: false,
                isFileLoaded: false,
                filePath: null,
                isFileError: false,
                fileName: '',
                percentage: 0,
                message: '',
                filePreview: '',
            }));

            handleRemovedFile({ fileName, docId });
        };

        const buildSpinner = () => {
            const className = `c100 green small center p${percentage}`;

            return (
                <div>
                    <div>
                        <div className="custom-file-uploader__spinner">
                            <div className={className}>
                                <span>{percentage}%</span>
                                <div className="slice">
                                    <div className="bar"></div>
                                    <div className="fill"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        const cutText = (text, max) => {
            if (text && text.length > max && !isNaN(max)) {
                return `${text.substr(0, max)}...`;
            }

            return text;
        };

        const showPreview = () => {
            if (customFileUploaderState.fileType === 'application/pdf') {
                return <img src={pdfImage} width="100" height="100" />;
            }

            return <img src={filePreview} width="150" height="100" />;
        };

        const dropZoneClassNames = classNames('custom-file-uploader__dropzone', {
            'custom-file-uploader__dropzone--uploaded': isFileLoaded,
            'custom-file-uploader__dropzone--uploading': isFileLoading,
            'custom-file-uploader__dropzone--error': isFileError,
        });
        const fileNameBlock =
            fileName !== '' ? <p className="custom-file-uploader__filename">{cutText(fileName, 30)}</p> : null;
        const deleteIcon = fileName ? (
            <div className="custom-file-uploader__delete">
                <a href="#" onClick={handleRemoveFile}>
                    <i className="icons icons--icon-delete-blue"></i>
                </a>
            </div>
        ) : null;
        const cancelIcon = isFileLoading ? (
            <div className="custom-file-uploader__cancel">
                <a href="#" onClick={handleCancelUploadFile}>
                    <i className="icons icons--icon-cancel-2"></i>
                </a>
            </div>
        ) : null;
        let loadAreaIcon;
        if (isFileLoaded) {
            loadAreaIcon = (
                <svg className="custom-file-uploader__checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                    <circle className="custom-file-uploader__checkmark--circle" cx="26" cy="26" r="25" fill="none" />
                    <path
                        className="custom-file-uploader__checkmark--check"
                        fill="none"
                        d="M14.1 27.2l7.1 7.2 16.7-16.8"
                    />
                </svg>
            );
        }

        let loadAreaClassName = 'custom-file-uploader__load-area';

        if (isFileLoading) {
            loadAreaIcon = buildSpinner();
        }

        if (fileDropped && !isFileLoading && filePreview !== '' && !isFileLoaded) {
            loadAreaClassName = 'custom-file-uploader__preview';
            loadAreaIcon = showPreview();
        }

        const fileSizeValidation = file => {
            if (file.size > MAX_FILE_SIZE_ALLOWED) {
                return {
                    code: 'file-wrong-size',
                    message: `uploads-wrongSize`,
                };
            }

            return null;
        };

        const fileTypeValidation = file => {
            const isFileTypeValid = fileType.includes(file.type);

            if (!isFileTypeValid) {
                return {
                    code: 'file-wrong-format',
                    message: `uploads-wrongFormat`,
                };
            }

            return null;
        };

        const dropzoneFileValidator = file => {
            const fileSizeValidationError = fileSizeValidation(file);
            const fileTypeValidationError = fileTypeValidation(file);

            if (fileSizeValidationError) {
                setCustomFileUploaderState(prevState => ({
                    ...prevState,
                    isFileError: true,
                    message: formatText('uploads-wrongSize'),
                    fileName: '',
                    filePreview: '',
                    filePath: null,
                }));
                handleValidation({ errorType: formatText('uploads-wrongSize'), id, docTitle: title });
            }
            if (fileTypeValidationError) {
                setCustomFileUploaderState(prevState => ({
                    ...prevState,
                    isFileError: true,
                    message: formatText('uploads-wrongFormat'),
                    fileName: '',
                    filePreview: '',
                    filePath: null,
                }));
                handleValidation({ errorType: formatText('uploads-wrongFormat'), id, docTitle: title });
            }

            if (fileSizeValidationError || fileTypeValidationError) {
                return [fileSizeValidationError, fileTypeValidationError];
            }

            setCustomFileUploaderState(prevState => ({
                ...prevState,
                message: '',
                fileName: '',
                fileType: '',
                fileDropped: true,
                isFileError: false,
            }));

            uploadFile(file);

            return null;
        };

        const { getRootProps, getInputProps, isDragActive, fileRejections, open } = useDropzone({
            useFsAccessApi: false,
            validator: dropzoneFileValidator,
        });

        const fileRejectionItems = fileRejections.map(({ file, errors }) => {
            return (
                <ul className="custom-file-uploader-errors-list" key={file.path}>
                    {errors.map(error => (
                        <li className="custom-file-uploader-error-item" key={error.code}>
                            {formatText(error.message)}
                        </li>
                    ))}
                </ul>
            );
        });

        const onClickDropzone = event => {
            doTagOnClickUploadBox(docId);
            event.stopPropagation();
            dropzoneRef.current = open;
            handleClicToShowModal(event, docId);
            if (isFileLoaded || (disabled && currentDisabled)) {
                event.preventDefault();
            }
        };
        const activeClassName = isDragActive ? 'custom-file-uploader__dropzone--hover' : '';

        const hasUploadFileAnError = isFileError;
        const isFileUploadBoxEmpty = !isFileError && !isFileLoaded && !fileDropped;
        const shouldCreateUploadBoxContent = hasUploadFileAnError || isFileUploadBoxEmpty;

        return (
            <div className="custom-file-uploader">
                <h5 className="custom-file-uploader__title">{title}</h5>
                <div
                    {...getRootProps({ onClick: onClickDropzone })}
                    className={`${dropZoneClassNames} ${activeClassName}`}
                >
                    <div>
                        <div className={loadAreaClassName}>
                            {loadAreaIcon}
                            {shouldCreateUploadBoxContent && (
                                <CreateUploadBoxContent
                                    isFileError={isFileError}
                                    shouldBeCaptureType={shouldBeCaptureType}
                                />
                            )}
                        </div>
                        {deleteIcon}
                        {cancelIcon}
                        {fileNameBlock}
                    </div>
                    {shouldBeCaptureType ? (
                        <input {...getInputProps()} capture="environment" />
                    ) : (
                        <input {...getInputProps()} />
                    )}
                </div>
                {fileRejectionItems}
            </div>
        );
    }
);

CustomFileUploaderComponent.displayName = 'CustomFileUploaderComponent';
